import { useQuery } from 'react-query';
import { useFilterToolbarState } from 'hooks/filterToolbar/useFilterToolbarState';
import { useScoutbotSavedSearchQueries } from 'hooks/projectMap/useScoutbotSavedSearchQueries';
import { useDebounce } from 'hooks/useDebounced';
import { fetchAddressList } from 'pages/ProjectInsights/services';
import { useAddressSearch, useSelectedPolygonBoundsOnMap } from 'state/map';
import { useMapBoundsAsCardinalDirections } from 'state/mapSettings';

/**
 * When a parcel is selected, fetches total project counts for both sidebar tabs:
 * — Match Filters: same filters + map context as the main map list, scoped to the parcel
 * — All Projects: every project in the parcel (no filters)
 *
 * Uses the same `fetchAddressList` / Elasticsearch API as `useMapProjectList` (page 1 only for totals).
 */
export const useParcelProjectTabCounts = () => {
  const [selectedPolygonBoundsOnMap] = useSelectedPolygonBoundsOnMap();
  const [mapBoundsAsCardinalDirections] = useMapBoundsAsCardinalDirections();
  const debouncedMapBounds = useDebounce(mapBoundsAsCardinalDirections, 800);
  const { filterFieldsState, getFiltersAsQueryParams, getMapStateAsQueryParams } =
    useFilterToolbarState();
  const [addressSearch] = useAddressSearch();
  const debouncedAddressSearch = useDebounce(addressSearch, 500);
  const { isScoutSearch, rawQueries } = useScoutbotSavedSearchQueries();

  const parcelSelected = !!selectedPolygonBoundsOnMap;

  const matchFiltersQuery = useQuery(
    [
      'map-parcel-tab-count',
      'match-filters',
      filterFieldsState,
      debouncedMapBounds,
      selectedPolygonBoundsOnMap,
      debouncedAddressSearch,
      isScoutSearch,
      rawQueries,
    ],
    () => {
      const allFilters = getFiltersAsQueryParams();
      const mapState = getMapStateAsQueryParams();
      // A scoutbot search's real filters live in raw_queries - the
      // individual filter fields stay empty, so only the date/status fields
      // (which the raw-queries path also applies) still apply on top of it.
      const filterState = isScoutSearch
        ? {
            raw_queries: rawQueries,
            startDate: allFilters.startDate,
            createdAtStart: allFilters.createdAtStart,
            createdAtEnd: allFilters.createdAtEnd,
            status: allFilters.status,
          }
        : allFilters;

      return fetchAddressList({
        ...filterState,
        ...mapState,
        pathOrBounds: selectedPolygonBoundsOnMap,
        page: 1,
      });
    },
    {
      enabled: parcelSelected,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  const allInParcelQuery = useQuery(
    ['map-parcel-tab-count', 'all-in-parcel', selectedPolygonBoundsOnMap],
    () =>
      fetchAddressList({
        pathOrBounds: selectedPolygonBoundsOnMap,
        page: 1,
      }),
    {
      enabled: parcelSelected,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    },
  );

  return {
    matchFiltersCount: matchFiltersQuery.data?.count,
    matchFiltersLoading: matchFiltersQuery.isLoading,
    matchFiltersError: matchFiltersQuery.isError,
    allInParcelCount: allInParcelQuery.data?.count,
    allInParcelLoading: allInParcelQuery.isLoading,
    allInParcelError: allInParcelQuery.isError,
  };
};
